import {
  Box,
  type BoxProps,
  Button,
  Flex,
  IconButton,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react'
import { faClose } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sortBy, uniqBy } from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import { POST_FEEDBACK_RATINGS, type PostFeedbackItems } from '@/client/services/worlds'

import type {
  PostDetails,
  PostEventDetails,
  PostFeedbackDetails,
} from '@/components/Community/Post/types'
import { useToaster } from '@/components/Toast/useToaster'
import Icon from '@/components/UI/Icon/Icon'

import { RATING_TYPE_TO_ICON } from './constants'

export default function PostFeedbackForm({
  post,
  postEvents,
  postFeedbacks,
  ...props
}: {
  post: PostDetails
  postEvents: PostEventDetails[]
  postFeedbacks: PostFeedbackDetails[]
} & BoxProps): JSX.Element | null {
  const user = useUser()
  const toast = useToaster()
  const [postFeedbackItems, setPostFeedbackItems] = useState<PostFeedbackItems>([])
  const [usersAlreadyReviewed, setUsersAlreadyReviewed] = useState<Set<number>>(new Set())

  useEffect(
    () => setUsersAlreadyReviewed(new Set(postFeedbacks.map(postFeedback => postFeedback.user.id))),
    [postFeedbacks]
  )

  // TODO: order these more intelligently?
  const usersToReview = useMemo(
    () =>
      sortBy(
        uniqBy(
          postEvents
            .map(postEvent => postEvent.user)
            .filter(
              postEventUser =>
                postEventUser.id !== user.id && !usersAlreadyReviewed.has(postEventUser.id)
            ),
          postEventUser => postEventUser.id
        ),
        postEventUser => postEventUser.id
      ),
    [postEvents, user.id, usersAlreadyReviewed]
  )

  useEffect(() => {
    setPostFeedbackItems(
      usersToReview.map(userToReview => ({
        userId: userToReview.id,
        rating: null,
        note: null,
        internalNote: null,
      }))
    )
    // oxlint-ignore-next-line react/exhaustive-deps
  }, [usersToReview.map(userToReview => userToReview.id).join(',')])

  const [submitFeedback] = useAPIWrapper(
    async ({ userIds, empty = false }: { userIds: number[]; empty?: boolean }) => {
      await RPC.submitPostFeedback({
        postId: post.id,
        feedback: postFeedbackItems
          .filter(item => userIds.includes(item.userId))
          .map(item =>
            empty
              ? { userId: item.userId, rating: null, note: null, internalNote: null }
              : {
                  userId: item.userId,
                  rating: item.rating,
                  note:
                    item.rating === 'Helpful' || item.rating === 'Super Helpful' ? item.note : null,
                  internalNote: item.rating === 'Not Helpful' ? item.internalNote : null,
                }
          ),
      })

      setUsersAlreadyReviewed(prevUsers => new Set([...prevUsers, ...userIds]))

      toast({
        title: 'Thank you for your feedback!',
        status: 'success',
      })
    },
    [post.id, postFeedbackItems, toast],
    'Submit Post Feedback'
  )

  const updateNote = (userId: number | null, note: string) => {
    setPostFeedbackItems(prevItems =>
      prevItems.map(item => (item.userId === userId ? { ...item, note } : item))
    )
  }

  const updateInternalNote = (userId: number | null, internalNote: string) => {
    setPostFeedbackItems(prevItems =>
      prevItems.map(item => (item.userId === userId ? { ...item, internalNote } : item))
    )
  }

  const userToReview = usersToReview[0] ?? null
  const postFeedbackItem = postFeedbackItems.find(item => item.userId === userToReview?.id) ?? null
  const isNotHelpful = postFeedbackItem?.rating === 'Not Helpful'

  return userToReview == null ? null : (
    <Box {...props}>
      <Stack
        direction="column"
        px={10}
        py={8}
        borderRadius="8px"
        align="center"
        bgColor="bg.muted"
        position="relative"
      >
        <Box
          position="absolute"
          width="36px"
          textAlign="right"
          fontSize="20px"
          top="10px"
          right="10px"
        >
          <IconButton
            variant="icon"
            cursor="pointer"
            borderRadius="50%"
            icon={<Icon size="md" faIcon={faClose} />}
            aria-label="Close post"
            _hover={{ bg: 'bg.emphasized' }}
            onClick={async () => {
              await submitFeedback({ userIds: usersToReview.map(u => u.id), empty: true })
            }}
          />
        </Box>

        <Stack maxWidth="400px" spacing={8}>
          <Stack spacing={4}>
            <Flex>
              <Text fontWeight={600}>How helpful was {userToReview.displayName}?</Text>
              <Spacer />
              {usersToReview.length > 1 ? (
                <Text>({usersToReview.length - 1} more to go)</Text>
              ) : null}
            </Flex>
            <SimpleGrid columns={3} spacing={4} mb={6}>
              {POST_FEEDBACK_RATINGS.map(rating => {
                const icon = RATING_TYPE_TO_ICON[rating]
                const isActive = postFeedbackItem?.rating === rating

                return (
                  <Stack
                    key={rating}
                    direction="column"
                    align="center"
                    textAlign="center"
                    rounded="md"
                    border={`2px solid ${chakraTokenToVar('border.emphasized')}`}
                    p={2}
                    bg={isActive ? 'primary.subtle' : 'bg'}
                    onClick={() => {
                      setPostFeedbackItems(prevItems =>
                        prevItems.map(item => {
                          if (item.userId === userToReview.id) {
                            return { ...item, rating: rating !== item.rating ? rating : null }
                          }
                          return item
                        })
                      )
                    }}
                    _hover={{ bg: isActive ? 'primary.subtle' : 'bg.emphasized' }}
                  >
                    <FontAwesomeIcon
                      icon={icon}
                      color={chakraTokenToVar('primary.fg')}
                      fontSize="28px"
                    />
                    <Text variant="supporting" color="primary.fg" fontSize="14px" fontWeight={600}>
                      {rating}
                    </Text>
                  </Stack>
                )
              })}
            </SimpleGrid>
          </Stack>

          <Stack spacing={4}>
            <Text fontWeight={600}>
              {isNotHelpful
                ? `How can ${userToReview.displayName} improve? Your feedback will only be seen by moderators.`
                : `Would you like to share a note of feedback or gratitude with ${userToReview.displayName}? This would mean a lot to them!`}
            </Text>

            <Textarea
              rows={2}
              onChange={e => {
                if (isNotHelpful) {
                  updateInternalNote(userToReview.id, e.target.value)
                } else {
                  updateNote(userToReview.id, e.target.value)
                }
              }}
              placeholder={
                isNotHelpful
                  ? 'Add your feedback here!'
                  : 'Thank you for the live Zoom help! I love how interactive it was.'
              }
              value={(isNotHelpful ? postFeedbackItem?.internalNote : postFeedbackItem?.note) ?? ''}
            />
          </Stack>
          <Flex flexDir="row" width="100%" maxWidth="400px">
            <Button
              variant="ghost"
              onClick={async () => {
                await submitFeedback({ userIds: [userToReview.id], empty: true })
              }}
            >
              Skip
            </Button>
            <Spacer />
            <Button
              onClick={async () => {
                await submitFeedback({ userIds: [userToReview.id] })
              }}
            >
              Submit
            </Button>
          </Flex>
        </Stack>
      </Stack>
    </Box>
  )
}
